import { FC, Dispatch, SetStateAction } from 'react';
import {
  WbAlert,
  WbButton,
  WbInfoTooltip,
  WbTextField,
} from '@agilelab/plugin-wb-platform';
import { RoleEntity } from '@agilelab/plugin-wb-rbac-common';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { AsyncState } from 'react-use/lib/useAsyncFn';
import { z } from 'zod';

const useStyles = makeStyles(
  theme => ({
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
    },
    actions: {
      display: 'flex',
      padding: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      gap: theme.spacing(2),
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
    },
    fields: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      paddingInline: theme.spacing(2),
      paddingTop: theme.spacing(2),
      overflowY: 'auto',
    },
    cloneAssignmentsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    checkboxLabel: {
      marginRight: 0,
    },
  }),
  { name: 'NewRoleDrawer' },
);

export const RoleFormSchema = z.object({
  displayName: z.string({
    required_error: 'Name is required',
  }),
  description: z.string().nullish(),
  cloneAssignments: z.boolean().optional(),
});

export interface RoleFormType {
  displayName: string;
  description?: string;
  cloneAssignments?: boolean;
}

export interface RoleFormProps {
  defaultValues?: RoleFormType;
  onSubmit: (newValues: RoleFormType) => void;
  dataState: AsyncState<RoleEntity[] | undefined>;
  setOpenDrawer: Dispatch<SetStateAction<boolean>>;
  submitLabel?: string;
  generalError?: Error;
  showCloneAssignments?: boolean;
}

export const RoleForm: FC<RoleFormProps> = ({
  onSubmit,
  dataState,
  setOpenDrawer,
  defaultValues,
  submitLabel,
  generalError,
  showCloneAssignments,
}) => {
  const classes = useStyles();
  const { handleSubmit, control, watch } = useForm<RoleFormType>({
    defaultValues,
    resolver: zodResolver(RoleFormSchema),
  });

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Box className={classes.fields}>
        {generalError && (
          <WbAlert
            style={{ margin: 0 }}
            variant="collapsible"
            message={{
              title: 'Error',
              type: 'error',
              content: <Typography>{generalError.message}</Typography>,
            }}
          />
        )}
        <Controller
          name="displayName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl style={{ width: '100%' }}>
              <WbTextField
                label="Name *"
                value={field.value}
                onChange={field.onChange}
                error={Boolean(error)}
                helperText={
                  error?.message ||
                  'Entering a meaningful and recognizable name'
                }
                style={{ width: '100%' }}
              />
            </FormControl>
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl style={{ width: '100%' }}>
              <WbTextField
                label="Description"
                value={field.value}
                error={Boolean(error)}
                onChange={field.onChange}
                style={{ width: '100%' }}
                helperText={error?.message || 'Provide a detailed description'}
              />
            </FormControl>
          )}
        />
        {showCloneAssignments && (
          <Controller
            name="cloneAssignments"
            control={control}
            render={({ field }) => (
              <FormControl style={{ width: '100%' }}>
                <Box className={classes.cloneAssignmentsRow}>
                  <FormControlLabel
                    classes={{ root: classes.checkboxLabel }}
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={e => field.onChange(e.target.checked)}
                        color="primary"
                        name="cloneAssignments"
                      />
                    }
                    label="Clone role assignments"
                  />
                  <WbInfoTooltip title="Mark this option if you want the new role to be assigned to the same users and groups as the source role." />
                </Box>
              </FormControl>
            )}
          />
        )}
      </Box>
      <Box className={classes.actions}>
        <WbButton
          onClick={() => {
            setOpenDrawer(false);
          }}
          variant="outlined"
          color="primary"
          label="Cancel"
        />
        <WbButton
          label={submitLabel ?? (defaultValues ? 'Edit Role' : 'Add Role')}
          type="submit"
          disabled={dataState.loading || !watch('displayName')}
          variant="contained"
          color="primary"
        />
      </Box>
    </form>
  );
};
